import React from 'react'
import { StyleSheet, View } from 'react-native'

import { useRouter } from 'expo-router'

import { api } from '@ppwr/convex/_generated/api'
import { useQuery } from 'convex/react'

import {
  DIAMOND_REASON_NAMES,
  DIAMOND_CATEGORY_LABELS,
  DIAMOND_REASON_CATEGORIES,
  type DiamondReason,
  type DiamondCategory
} from '@ppwr/convex/diamonds/catalog'
import { BADGES, BADGE_CATEGORY_LABELS, isBadgeId } from '@ppwr/convex/badges/catalog'

import { formatDistanceToNowStrict } from 'date-fns'
import { numberWithCommas } from '@/utils/numberWithCommas'

import { DS } from '@/design-system'

import Diamond from '@/assets/diamonds/Diamond.svg'
import { NavCard } from '@/screens/diamonds/components/NavCard'

const BADGE_TIER_BY_REASON: Partial<Record<DiamondReason, string>> = {
  badge_tier_bronze: 'Bronze',
  badge_tier_silver: 'Silver',
  badge_tier_gold: 'Gold',
  badge_tier_platinum: 'Platinum'
}

interface RecentActivityCardProps {
  compact?: boolean
}

export function RecentActivityCard({ compact = false }: RecentActivityCardProps) {
  const router = useRouter()
  const ledger = useQuery(api.diamonds.queries.getDiamondEvents, {})

  const ledgerDescending = ledger ? [...ledger.events].reverse().slice(0, compact ? 10 : undefined) : null

  const showHeader = compact == true ? true : false
  const isClickable = compact == true ? true : false
  const scrollEnabled = compact == true ? false : true

  return (
    <NavCard
      title="Recent Activity"
      handler={() => router.push('/diamonds/recent-activity')}
      showHeader={showHeader}
      isClickable={isClickable}
      scrollEnabled={scrollEnabled}
    >
      <>

        {ledgerDescending == null && null}

        {ledgerDescending && ledgerDescending.length === 0 && (
          <DS.Text variant="Body Small/Regular" textAlign="center" color="Brand/Dark Grey" style={styles.msg}>
            No activity yet — play a game to start earning diamonds
          </DS.Text>
        )}
        
        {ledgerDescending && ledgerDescending.length > 0 &&
          ledgerDescending.map((event, index) => {
            const borderTop = index > 0 ? { borderTopWidth: 1, borderTopColor: DS.palette['Brand/Light Grey'] } : {}
            const badgeTier = BADGE_TIER_BY_REASON[event.reason as DiamondReason]
            // legacy badge rows carry the badgeId in refId
            const badgeId = event.badgeId ?? (event.category === 'badge' ? event.refId : undefined)
            const badge = badgeTier && badgeId && isBadgeId(badgeId) ? BADGES[badgeId] : null
            // one-shot badges have no tier to show
            const title = badge
              ? badge.tiers.length === 1
                ? badge.name
                : `${badgeTier}: ${badge.name}`
              : (DIAMOND_REASON_NAMES[event.reason as DiamondReason] ?? event.reason)
            // badge tiers show the game that earned them (legacy badge rows: the badge's own category);
            // other rows show their reason's kind, so a streak day filed with a Pocket Pick says Streaks
            const reasonCategory = badgeTier
              ? undefined
              : (DIAMOND_REASON_CATEGORIES as Partial<Record<string, DiamondCategory>>)[event.reason]
            const subtitle =
              badge && event.category === 'badge'
                ? BADGE_CATEGORY_LABELS[badge.category]
                : (DIAMOND_CATEGORY_LABELS[(reasonCategory ?? event.category) as DiamondCategory] ?? event.category)

            return (
              <View
                key={event._id}
                style={[
                  borderTop,
                  { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 12 }
                ]}
              >
                <View style={{ gap: 2 }}>
                  <DS.Text variant="Body Small/Medium" color="Global/Black">
                    {title}
                  </DS.Text>
                  <DS.Text variant="Caption/Regular" color="Brand/Dark Grey">
                    {subtitle}
                  </DS.Text>
                </View>
                <View style={{ gap: 2 }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', gap: 4 }}>
                    <Diamond height="120%" preserveAspectRatio="xMaxYMid meet" />
                    <DS.Text variant="Body Small/Medium">
                      {event.amount > 0 ? '+' : ''}
                      {numberWithCommas(event.amount)}
                    </DS.Text>
                  </View>
                  <DS.Text variant="Mini/Medium" color="Brand/Dark Grey">
                    {formatDistanceToNowStrict(new Date(event.createdAt), { addSuffix: true })}
                  </DS.Text>
                </View>
              </View>
            )
          })}
      </>
    </NavCard>
  )
}

const styles = StyleSheet.create({
  card: {
    backgroundColor: DS.palette['Global/White'],
    borderRadius: DS.spacing[8],
    padding: DS.spacing[16],
    gap: 8
  },
  msg: {
    padding: DS.spacing[8],
    alignItems: 'center'
  }
})
